/**
 * Mock Settings API
 * This provides a mock implementation of the settings API
 * Replace these mocks with real API calls when backend is ready
 */

// Mock data store
const mockSettings = {
  marketplace: {
    general: {
      maintenanceMode: false,
      defaultCurrency: 'USD',
      defaultLanguage: 'en',
      productVisibility: 'public'
    },
    vendor: {
      selfSignupAllowed: true,
      approvalWorkflow: 'manual',
      requiredFields: ['companyName', 'email', 'phone', 'address'],
      defaultRatingWeight: 1.0
    },
    products: {
      categoriesEnabled: true,
      featuredSlots: 10,
      allowAttachments: true,
      maxAttachmentSize: 10
    },
    pricing: {
      commissionPercentage: 5,
      transactionFee: 2.50,
      taxEnabled: true,
      defaultTaxRate: 8.5
    },
    lastModified: new Date().toISOString(),
    lastModifiedBy: 'admin@evoltbay.com'
  },
  enterprise: {
    rfqDefaults: {
      defaultDeadlineDays: 14,
      defaultResponseWindowDays: 7,
      defaultVisibility: 'certified-vendors',
      autoPublish: false,
      requireAttachments: true,
      maxAttachments: 10,
      allowAnonymousRfqs: false
    },
    quoteWorkflows: {
      requirePoUpload: true,
      poApprovalRequired: true,
      signatureProvider: 'docusign',
      quoteExpirationDays: 30,
      autoAcceptThreshold: null,
      revisionLimit: 3,
      digitalSignatureRequired: true
    },
    roiSimulator: {
      defaultDegradationRate: 0.8,
      defaultElectricityCost: 0.13,
      defaultMaintenanceCost: 150,
      defaultInsuranceCost: 200,
      calculationMethod: 'npv',
      taxIncentivesEnabled: true,
      defaultTaxIncentiveRate: 26
    },
    permissions: {
      vendorCanEditQuotes: true,
      vendorCanWithdrawQuotes: true,
      buyerApprovalSteps: 2,
      maxQuoteValue: null,
      requireAdminReviewAbove: 100000,
      allowDirectNegotiation: true
    },
    notifications: {
      notifyOnNewRfq: true,
      notifyOnQuoteSubmitted: true,
      notifyOnQuoteAccepted: true,
      notifyOnQuoteExpired: false,
      emailDigestFrequency: 'daily',
      pushNotificationsEnabled: true,
      smsNotificationsEnabled: false,
      emailTemplates: {
        rfqCreated: 'New RFQ "{{rfqTitle}}" has been created. Deadline: {{deadline}}',
        quoteSubmitted: 'Vendor {{vendorName}} submitted a quote for "{{rfqTitle}}"',
        quoteAccepted: 'Congratulations! Your quote for "{{rfqTitle}}" has been accepted.',
        quoteRejected: 'Your quote for "{{rfqTitle}}" was not selected. Reason: {{reason}}'
      }
    },
    reporting: {
      enableCsvExport: true,
      enablePdfReports: true,
      dataRetentionDays: 2555,
      autoArchiveCompletedRfqs: true,
      archiveAfterDays: 90,
      analyticsEnabled: true,
      trackVendorPerformance: true
    },
    security: {
      requireAdminReviewForHighValue: true,
      highValueThreshold: 50000,
      encryptDocuments: true,
      requireMfa: false,
      auditLogRetentionDays: 2555,
      ipWhitelistEnabled: false,
      allowedIpRanges: []
    },
    compliance: {
      requireTermsAcceptance: true,
      requireNda: false,
      documentRetentionYears: 7,
      gdprCompliant: true,
      hipaaCompliant: false,
      soc2Compliant: false
    },
    lastModified: new Date().toISOString(),
    lastModifiedBy: 'admin@evoltbay.com'
  },
  government: {
    general: {
      workflowMode: 'open',
      defaultPostingWindowDays: 30,
      minimumBidWindowDays: 10,
      procurementThreshold: 10000,
      allowEmergencyProcurement: true,
      publicNoticeRequired: true,
      bidBondRequired: false,
      performanceBondRequired: true
    },
    vendorVerification: {
      certificationRequired: true,
      requiredDocuments: ['company_registration', 'tax_id', 'insurance_certificate'],
      autoVerificationEnabled: false,
      verificationTimeoutDays: 5,
      samRegistrationRequired: true,
      cageCodeRequired: true,
      dunsNumberRequired: true,
      gsaSchedulePreferred: true,
      securityClearanceRequired: false
    },
    procurementRules: {
      bidOpeningPolicy: 'scheduled',
      allowSeatedBids: true,
      allowElectronicBids: true,
      tieBreakingMethod: 'lowest-price',
      localPreferenceEnabled: false,
      localPreferencePercentage: 5,
      smallBusinessPreferenceEnabled: true,
      smallBusinessPreferencePercentage: 10,
      veteranOwnedPreferenceEnabled: true,
      veteranOwnedPreferencePercentage: 5,
      awardCriteria: {
        price: 40,
        technicalCapability: 30,
        pastPerformance: 20,
        deliveryTime: 10
      }
    },
    agencyManagement: {
      multiTierApprovalRequired: true,
      approvalTiers: 3,
      agencyTypes: ['Federal', 'State', 'Local', 'Military'],
      contractingOfficerApprovalRequired: true,
      legalReviewRequired: true,
      legalReviewThreshold: 100000,
      budgetOfficerApprovalRequired: true,
      chiefApprovalThreshold: 500000
    },
    contractTemplates: {
      useStandardTemplates: true,
      allowCustomClauses: false,
      requiredClauses: ['FAR 52.212-4', 'FAR 52.212-5', 'Equal Opportunity'],
      forbiddenClauses: ['Arbitration', 'Non-Compete'],
      templateApprovalRequired: true,
      contractTypeDefaults: {
        fixedPrice: true,
        costReimbursement: false,
        timeAndMaterials: true,
        indefiniteDelivery: false
      }
    },
    complianceAudit: {
      auditTrailEnabled: true,
      detailedLoggingEnabled: true,
      retentionPeriodYears: 7,
      quarterlyReportsRequired: true,
      annualAuditsRequired: true,
      externalAuditorsAllowed: false,
      farComplianceChecks: true,
      whistleblowerProtection: true,
      fraudDetectionEnabled: true
    },
    securityAccess: {
      clearanceLevels: ['Public', 'Confidential', 'Secret'],
      documentClassificationEnabled: true,
      encryptionRequired: true,
      vpnRequired: false,
      ipRestrictionEnabled: false,
      allowedIpRanges: [],
      twoFactorRequired: true,
      sessionTimeoutMinutes: 30,
      maxConcurrentSessions: 2,
      geoRestrictionEnabled: false,
      allowedCountries: ['US']
    },
    lastModified: new Date().toISOString(),
    lastModifiedBy: 'admin@evoltbay.com'
  },
  account: {
    userManagement: {
      allowSelfRegistration: true,
      requireEmailVerification: true,
      defaultUserRole: 'user',
      bulkUserOperations: true,
      userActivationWorkflow: 'auto',
      accountDeactivationPolicy: 'manual',
      welcomeEmailEnabled: true,
      profileCompletionRequired: false
    },
    authentication: {
      passwordMinLength: 8,
      passwordRequireUppercase: true,
      passwordRequireLowercase: true,
      passwordRequireNumbers: true,
      passwordRequireSymbols: false,
      passwordExpirationDays: 90,
      sessionTimeoutMinutes: 30,
      maxLoginAttempts: 5,
      lockoutDurationMinutes: 15,
      rememberMeEnabled: true,
      rememberMeDurationDays: 7
    },
    multiFactorAuth: {
      mfaRequired: false,
      mfaMethods: ['app', 'sms'],
      mfaGracePeriodDays: 7,
      backupCodesEnabled: true,
      smsMfaEnabled: true,
      appMfaEnabled: true,
      hardwareTokenEnabled: false
    },
    profileSettings: {
      defaultAvatarEnabled: true,
      avatarUploadAllowed: true,
      maxAvatarSizeKb: 512,
      profileVisibilityOptions: ['public', 'private', 'contacts'],
      defaultProfileVisibility: 'public',
      bioRequired: false,
      bioMaxLength: 500,
      socialLinksEnabled: true,
      locationDisplayEnabled: true,
      timezoneRequired: false
    },
    permissions: {
      roleBasedAccessControl: true,
      customRolesEnabled: true,
      permissionInheritance: true,
      granularPermissions: true,
      permissionMatrix: {
        admin: ['all'],
        manager: ['users.manage', 'content.manage', 'reports.view'],
        user: ['profile.edit', 'content.view'],
        vendor: ['products.manage', 'orders.view'],
        enterprise: ['rfq.create', 'quotes.submit', 'reports.view'],
        agency: ['rfq.create', 'procurement.manage', 'compliance.view']
      },
      approvalWorkflows: true,
      delegationEnabled: true,
      emergencyAccessEnabled: false
    },
    systemPreferences: {
      defaultTimezone: 'UTC',
      defaultDateFormat: 'MM/DD/YYYY',
      defaultTimeFormat: '12h',
      defaultLanguage: 'en',
      emailNotificationsEnabled: true,
      pushNotificationsEnabled: true,
      smsNotificationsEnabled: false,
      weeklyDigestEnabled: true,
      maintenanceNotifications: true,
      securityAlertsEnabled: true
    },
    apiManagement: {
      apiAccessEnabled: true,
      apiKeyRotationDays: 90,
      rateLimitRequestsPerMinute: 60,
      rateLimitRequestsPerHour: 1000,
      webhookEnabled: true,
      webhookRetries: 3,
      webhookTimeoutSeconds: 30,
      apiDocumentationEnabled: true,
      apiVersioningEnabled: true,
      deprecatedApiWarnings: true
    },
    auditLogs: {
      auditLoggingEnabled: true,
      logRetentionDays: 2555,
      sensitiveDataMasking: true,
      exportAuditLogs: true,
      realTimeMonitoring: true,
      securityEventAlerts: true,
      complianceReporting: true,
      userActivityTracking: true,
      loginLogoutTracking: true,
      dataAccessLogging: true
    },
    lastModified: new Date().toISOString(),
    lastModifiedBy: 'admin@evoltbay.com'
  }
}

export class SettingsApi {
  /**
   * Get settings for a specific module
   */
  static async get(module: 'marketplace' | 'enterprise' | 'government' | 'account'): Promise<any> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500))
    
    return {
      success: true,
      data: mockSettings[module]
    }
  }

  /**
   * Update settings for a specific module
   */
  static async put(module: 'marketplace' | 'enterprise' | 'government' | 'account', data: any): Promise<any> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800))
    
    // Update mock store
    mockSettings[module] = {
      ...mockSettings[module],
      ...data,
      lastModified: new Date().toISOString(),
      lastModifiedBy: 'admin@evoltbay.com'
    }
    
    return {
      success: true,
      message: 'Settings updated successfully',
      data: mockSettings[module]
    }
  }

  /**
   * Reset settings to defaults
   */
  static async reset(_module: 'marketplace' | 'enterprise' | 'government' | 'account'): Promise<any> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500))
    
    return {
      success: true,
      message: 'Settings reset to defaults'
    }
  }
}

/**
 * Instructions for replacing mocks with real API:
 * 
 * 1. Replace SettingsApi.get() with:
 *    await apiService.get(`/api/admin-settings/${module}`)
 * 
 * 2. Replace SettingsApi.put() with:
 *    await apiService.put(`/api/admin-settings/${module}`, data)
 * 
 * 3. Replace SettingsApi.reset() with:
 *    await apiService.post(`/api/admin-settings/${module}/reset`)
 */

